// client/src/components/configuracoes/horarios/ConfiguracoesAgenda.js - 100% COMPLETO E PADRONIZADO
import React, { useState, useEffect } from 'react';
import useAgendas from '../../../hooks/useAgendas';
import { useUnidades } from '../../../hooks/useUnidades'; 
import useAlert from '../../../hooks/useAlert';
import { Save, Loader2, Calendar, Lock } from 'lucide-react';

const extractIdStr = (obj) => {
    if (!obj) return null;
    if (typeof obj === 'string') return obj;
    const id = obj._id || obj.id;
    return id ? String(id) : null;
};

const PrazoInput = ({ prazo, onPrazoChange }) => (
    <div className="input-group flex">
        <input 
            type="number" 
            name="valor" 
            value={prazo?.valor || ''} 
            onChange={onPrazoChange} 
            className="form-input flex-1" 
            min="0"
            style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
        />
        <select 
            name="unidade" 
            value={prazo?.unidade || 'horas'} 
            onChange={onPrazoChange} 
            className="form-input" 
            style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0, width: '120px' }}
        >
            <option value="minutos">Minutos</option>
            <option value="horas">Horas</option>
            <option value="dias">Dias</option>
        </select>
    </div>
);

const ConfiguracoesAgenda = ({ entidadeSelecionada }) => {
    const { setAlert } = useAlert();
    
    const { useAgendaByEntidadeQuery, updateAgenda } = useAgendas();
    const { data: unidadesResponse } = useUnidades({ ativo: true });
    
    const unidades = Array.isArray(unidadesResponse) ? unidadesResponse : (unidadesResponse?.data || []);
    
    const { id: entidadeId, tipo: tipoEntidade, nome: nomeEntidade } = entidadeSelecionada || {};

    const { data: agendaData, isLoading: loadingAgenda } = useAgendaByEntidadeQuery(tipoEntidade, entidadeId, {
        enabled: !!entidadeId && !!tipoEntidade
    });

    const [formData, setFormData] = useState({
        nomeExibicao: '',
        permiteAgendamentoOnline: true,
        intervaloGradeHorarios: 60,
        horarioInicio: 7,
        horarioFim: 22,
        prazoCancelamentoSemPagamento: { valor: 24, unidade: 'horas' },
        acaoCancelamentoSemPagamento: 'Ser Avisado',
        acaoConclusaoAgendamento: 'Manual',
        prazoConclusaoAutomatica: { valor: 2, unidade: 'horas' },
        disponibilidadeFuturaEmDias: 30,
        antecedenciaMinimaParaAgendar: { valor: 1, unidade: 'horas' },
        permiteCancelamentoOnline: 'Automático',
        prazoMaximoCancelamentoOnline: { valor: 24, unidade: 'horas' },
        visibilidade: 'publica_na_unidade', 
        unidadesVinculadas: [], 
        corAgenda: '#3788d8',
        exibirNaAgenda: true
    });

    const isLoading = updateAgenda.isPending || loadingAgenda;

    useEffect(() => {
        if (entidadeId && !loadingAgenda) {
            const fetchedAgenda = agendaData?.data || agendaData;
            
            if (fetchedAgenda && (fetchedAgenda.id || fetchedAgenda._id)) {
                const agendaInfo = fetchedAgenda.configuracoes || {};
                const permissoesInfo = fetchedAgenda.permissoes || {};
                const metaInfo = fetchedAgenda.metadata || {};

                setFormData({
                    nomeExibicao: fetchedAgenda.nomeExibicao || `Agenda de ${nomeEntidade}`,
                    permiteAgendamentoOnline: agendaInfo.permiteAgendamentoOnline !== false,
                    intervaloGradeHorarios: Number(agendaInfo.intervaloGradeHorarios) || 60,
                    horarioInicio: Number(agendaInfo.horarioInicio) ?? 7,
                    horarioFim: Number(agendaInfo.horarioFim) ?? 22,
                    
                    prazoCancelamentoSemPagamento: {
                        valor: Number(agendaInfo.prazoCancelamentoSemPagamento?.valor) || 0,
                        unidade: agendaInfo.prazoCancelamentoSemPagamento?.unidade || 'horas'
                    },
                    acaoCancelamentoSemPagamento: agendaInfo.acaoCancelamentoSemPagamento || 'Ser Avisado',
                    acaoConclusaoAgendamento: agendaInfo.acaoConclusaoAgendamento || 'Manual',
                    prazoConclusaoAutomatica: {
                        valor: Number(agendaInfo.prazoConclusaoAutomatica?.valor) || 2,
                        unidade: agendaInfo.prazoConclusaoAutomatica?.unidade || 'horas'
                    },
                    disponibilidadeFuturaEmDias: Number(agendaInfo.disponibilidadeFuturaEmDias) || 30,
                    antecedenciaMinimaParaAgendar: {
                        valor: Number(agendaInfo.antecedenciaMinimaParaAgendar?.valor) || 1,
                        unidade: agendaInfo.antecedenciaMinimaParaAgendar?.unidade || 'horas'
                    },
                    permiteCancelamentoOnline: agendaInfo.permiteCancelamentoOnline || 'Automático',
                    prazoMaximoCancelamentoOnline: {
                        valor: Number(agendaInfo.prazoMaximoCancelamentoOnline?.valor) || 0,
                        unidade: agendaInfo.prazoMaximoCancelamentoOnline?.unidade || 'horas'
                    },
                    visibilidade: permissoesInfo.visibilidade || 'publica_na_unidade',
                    unidadesVinculadas: (permissoesInfo.unidadesVinculadas || []).map(extractIdStr),
                    corAgenda: metaInfo.corAgenda || '#3788d8',
                    exibirNaAgenda: metaInfo.exibirNaAgenda !== false
                });
            } else {
                setFormData(prev => ({
                    ...prev,
                    nomeExibicao: `Agenda de ${nomeEntidade}`,
                    unidadesVinculadas: tipoEntidade === 'Unidade' ? [extractIdStr(entidadeId)] : []
                }));
            }
        }
    }, [agendaData, entidadeId, nomeEntidade, loadingAgenda, tipoEntidade]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        let finalValue = value;
        
        if (type === 'checkbox') {
            finalValue = checked;
        } else if (type === 'number' || ['intervaloGradeHorarios'].includes(name)) {
            finalValue = value === '' ? 0 : Number(value);
            if (name === 'horarioInicio' || name === 'horarioFim') {
                if (finalValue < 0) finalValue = 0;
                if (finalValue > 23) finalValue = 23;
            }
        }
        
        setFormData(prev => ({ ...prev, [name]: finalValue }));
    };

    const onPrazoChange = (parentKey, e) => {
        const { name, value } = e.target;
        const valorFinal = name === 'valor' && value !== '' ? parseInt(value, 10) : value;
        setFormData(prev => ({ 
            ...prev, 
            [parentKey]: { 
                ...(prev[parentKey] || {}), 
                [name]: valorFinal 
            }
        }));
    };

    const handleUnidadeToggle = (unidadeId) => {
        if (tipoEntidade === 'Unidade' || tipoEntidade === 'User') return; // Bloqueia toggle
        
        const uIdStr = String(unidadeId);
        setFormData(prev => {
            const isSelected = prev.unidadesVinculadas.includes(uIdStr);
            return {
                ...prev,
                unidadesVinculadas: isSelected
                    ? prev.unidadesVinculadas.filter(id => id !== uIdStr)
                    : [...prev.unidadesVinculadas, uIdStr]
            };
        });
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!entidadeId || !tipoEntidade) return;

        if (!formData.nomeExibicao?.trim()) {
            setAlert('O Nome de Exibição da agenda é obrigatório.', 'warning');
            return;
        }

        try {
            const payload = {
                tipoEntidade: tipoEntidade,
                entidadeId: entidadeId,
                nomeExibicao: formData.nomeExibicao.trim(),
                configuracoes: {
                    permiteAgendamentoOnline: formData.permiteAgendamentoOnline,
                    intervaloGradeHorarios: Number(formData.intervaloGradeHorarios),
                    horarioInicio: Number(formData.horarioInicio),
                    horarioFim: Number(formData.horarioFim),
                    prazoCancelamentoSemPagamento: {
                        valor: Number(formData.prazoCancelamentoSemPagamento.valor),
                        unidade: formData.prazoCancelamentoSemPagamento.unidade
                    },
                    acaoCancelamentoSemPagamento: formData.acaoCancelamentoSemPagamento,
                    acaoConclusaoAgendamento: formData.acaoConclusaoAgendamento,
                    prazoConclusaoAutomatica: {
                        valor: Number(formData.prazoConclusaoAutomatica.valor),
                        unidade: formData.prazoConclusaoAutomatica.unidade
                    },
                    disponibilidadeFuturaEmDias: Number(formData.disponibilidadeFuturaEmDias),
                    antecedenciaMinimaParaAgendar: {
                        valor: Number(formData.antecedenciaMinimaParaAgendar.valor),
                        unidade: formData.antecedenciaMinimaParaAgendar.unidade
                    },
                    permiteCancelamentoOnline: formData.permiteCancelamentoOnline,
                    prazoMaximoCancelamentoOnline: {
                        valor: Number(formData.prazoMaximoCancelamentoOnline.valor),
                        unidade: formData.prazoMaximoCancelamentoOnline.unidade
                    }
                },
                permissoes: {
                    visibilidade: formData.visibilidade,
                    unidadesVinculadas: formData.visibilidade === 'publica_na_unidade' ? formData.unidadesVinculadas : []
                },
                metadata: {
                    corAgenda: formData.corAgenda,
                    exibirNaAgenda: formData.exibirNaAgenda
                }
            };

            await updateAgenda.mutateAsync({
                entidadeId: entidadeId,
                agendaData: payload
            });

            setAlert('Configurações da agenda salvas com sucesso!', 'success');
        } catch (err) {
            console.error("Falha ao salvar agenda:", err);
            setAlert(err.response?.data?.error || 'Erro ao salvar configurações', 'danger');
        }
    };

    if (loadingAgenda) {
        return <div className="panel-state-wrapper flex justify-center py-12"><Loader2 className="animate-spin text-primary" size={32} /></div>;
    }

    return (
        <form onSubmit={onSubmit} noValidate className="mt-6 form-content">
            <fieldset className="form-section">
                <legend className="form-section-title">
                    <Calendar size={18} className="inline mr-2"/>
                    Configurações Gerais e Exibição
                </legend>
                <div className="form-grid-container">
                    
                    <div className="form-group md:col-span-2 pb-4 mb-2 border-b border-gray-100">
                        <label className="required font-medium text-gray-800">Nome de Exibição da Agenda</label>
                        <input 
                            type="text" 
                            name="nomeExibicao" 
                            value={formData.nomeExibicao} 
                            onChange={handleChange} 
                            className="form-input mt-1 md:w-2/3"
                            placeholder='Ex: Agenda Dr. João'
                            required
                        />
                        <p className="text-xs text-gray-500 mt-1">Este é o nome que aparecerá no calendário principal.</p>
                    </div>

                    <div className="form-group">
                        <label>Cor da Agenda</label>
                        <div className="flex items-center gap-2">
                            <input 
                                type="color" 
                                name="corAgenda" 
                                value={formData.corAgenda} 
                                onChange={handleChange} 
                                className="w-10 h-8 rounded cursor-pointer"
                            />
                            <span className="text-sm text-gray-600">
                                {formData.corAgenda}
                            </span>
                        </div>
                    </div>

                    <div className="form-group md:col-span-2">
                        <label className="checkbox-label">
                            <input 
                                type="checkbox" 
                                name="exibirNaAgenda" 
                                checked={formData.exibirNaAgenda} 
                                onChange={handleChange} 
                                className="form-checkbox"
                            />
                            <span className="checkbox-label-text">Exibir {tipoEntidade === 'User' ? 'profissional' : 'entidade'} na agenda principal da recepção</span>
                        </label>
                    </div>
                    
                    <div className="form-group md:col-span-2 mt-2">
                        <label className="font-medium text-gray-800">Visibilidade (Controle de Acesso ABAC)</label>
                        <select 
                            name="visibilidade" 
                            value={formData.visibilidade} 
                            onChange={handleChange} 
                            className="form-input md:w-1/2 mt-1"
                        >
                            <option value="publica_na_unidade">Pública na Unidade (Padrão)</option>
                            <option value="publica_na_conta">Pública em toda a Conta</option>
                            <option value="privada">Privada (Apenas equipe autorizada)</option>
                        </select>

                        {formData.visibilidade === 'publica_na_unidade' && (
                            <div className="mt-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                                {tipoEntidade === 'Unidade' ? (
                                    <div className="flex flex-col gap-1 p-3 bg-blue-50 text-blue-800 rounded border border-blue-100">
                                        <div className="flex items-center font-medium">
                                            <Lock size={16} className="mr-2"/> Unidade Vinculada Automaticamente
                                        </div>
                                        <p className="text-sm">A agenda de uma unidade é exclusiva dela mesma e não pode ser vinculada manualmente a outras unidades.</p>
                                    </div>
                                ) : tipoEntidade === 'User' ? (
                                    <div className="flex flex-col gap-1 p-3 bg-blue-50 text-blue-800 rounded border border-blue-100">
                                        <div className="flex items-center font-medium">
                                            <Lock size={16} className="mr-2"/> Vínculo Gerenciado pelo Sistema
                                        </div>
                                        <p className="text-sm">A visibilidade desta agenda é herdada automaticamente das unidades em que este profissional atua (Aba Unidades e Serviços).</p>
                                    </div>
                                ) : (
                                    <>
                                        <label className="text-sm font-medium text-gray-700 mb-3 block">
                                            Selecione as Unidades Vinculadas:
                                        </label>
                                        {unidades.length > 0 ? (
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-40 overflow-y-auto pr-2">
                                                {unidades.map(u => {
                                                    const uId = String(u._id || u.id);
                                                    const isChecked = formData.unidadesVinculadas.includes(uId);
                                                    return (
                                                        <label key={uId} className="flex items-center gap-3 text-sm text-gray-700 cursor-pointer">
                                                            <input 
                                                                type="checkbox"
                                                                className="rounded border-gray-300 text-primary w-4 h-4"
                                                                checked={isChecked}
                                                                onChange={() => handleUnidadeToggle(uId)}
                                                            />
                                                            <span className={isChecked ? "font-medium text-primary" : ""}>
                                                                {u.nome}
                                                            </span>
                                                        </label>
                                                    )
                                                })}
                                            </div>
                                        ) : (
                                            <p className="text-xs text-gray-500">Nenhuma unidade encontrada.</p>
                                        )}
                                        <p className="text-xs text-gray-500 mt-3 border-t border-gray-200 pt-2">
                                            Apenas usuários com acesso a essas unidades poderão visualizar a agenda.
                                        </p>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </fieldset>

            <fieldset className="form-section">
                <legend className="form-section-title">Grade e Horários</legend>
                <div className="form-grid-container">
                    <div className="form-group">
                        <label>Intervalo da Grade (minutos)</label>
                        <select 
                            name="intervaloGradeHorarios" 
                            value={formData.intervaloGradeHorarios} 
                            onChange={handleChange} 
                            className="form-input"
                        >
                            <option value="10">10 min</option>
                            <option value="15">15 min</option>
                            <option value="20">20 min</option>
                            <option value="30">30 min</option>
                            <option value="45">45 min</option>
                            <option value="60">60 min</option>
                            <option value="120">120 min</option>
                            <option value="180">180 min</option>
                            <option value="360">360 min</option>
                        </select>
                        <p className="form-hint">Define o "passo" da sua agenda visual.</p>
                    </div>
                    
                    <div className="form-group">
                        <label>Horário Início da Grade</label>
                        <input
                            type="number"
                            name="horarioInicio"
                            min="0"
                            max="23"
                            value={formData.horarioInicio}
                            onChange={handleChange}
                            className="form-input"
                        />
                        <p className="form-hint">Hora inicial para exibição (0-23)</p>
                    </div>
                    
                    <div className="form-group">
                        <label>Horário Fim da Grade</label>
                        <input
                            type="number"
                            name="horarioFim"
                            min="0"
                            max="23"
                            value={formData.horarioFim}
                            onChange={handleChange}
                            className="form-input"
                        />
                        <p className="form-hint">Hora final para exibição (0-23)</p>
                    </div>
                </div>
            </fieldset>

            <fieldset className="form-section">
                <legend className="form-section-title">Agendamento pelo Cliente (Portal)</legend>
                <div className="form-group-checkbox mb-4">
                    <input 
                        type="checkbox" 
                        name="permiteAgendamentoOnline" 
                        id="permiteAgendamentoOnline" 
                        checked={formData.permiteAgendamentoOnline} 
                        onChange={handleChange} 
                        className="form-checkbox w-4 h-4" 
                    />
                    <label htmlFor="permiteAgendamentoOnline" className="ml-2 font-medium text-gray-800">
                        Permitir que clientes agendem sozinhos online
                    </label>
                    <p className="text-xs text-gray-500 ml-6 mt-1">Ao desativar, só poderá ser agendado pela recepção.</p>
                </div>
                
                <div className="form-grid-container">
                    <div className="form-group">
                        <label>Visibilidade da agenda (em dias)</label>
                        <input 
                            type="number" 
                            name="disponibilidadeFuturaEmDias" 
                            value={formData.disponibilidadeFuturaEmDias} 
                            onChange={handleChange} 
                            className="form-input"
                            min="1"
                            max="365"
                        />
                        <p className="form-hint">Quantos dias no futuro o cliente pode ver a agenda.</p>
                    </div>
                    
                    <div className="form-group">
                        <label>Antecedência mínima para agendar</label>
                        <PrazoInput 
                            prazo={formData.antecedenciaMinimaParaAgendar} 
                            onPrazoChange={(e) => onPrazoChange('antecedenciaMinimaParaAgendar', e)} 
                        />
                        <p className="form-hint">Quanto tempo antes o cliente deve marcar.</p>
                    </div>
                </div>
            </fieldset>
            
            <fieldset className="form-section">
                <legend className="form-section-title">Regras de Cancelamento e Prazos</legend>
                <div className="form-grid-container">
                    <div className="form-group md:col-span-2 border-b border-gray-100 pb-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-3">
                                <div>
                                    <label className="text-sm font-medium text-gray-700 block mb-1">Cancelamento pelo cliente</label>
                                    <select 
                                        name="permiteCancelamentoOnline" 
                                        value={formData.permiteCancelamentoOnline} 
                                        onChange={handleChange} 
                                        className="form-input"
                                    >
                                        <option value="Não Permitir">Não Permitir Cancelamento</option>
                                        <option value="Aprovação Necessária">Requer Aprovação</option>
                                        <option value="Automático">Automático</option>
                                    </select>
                                </div>

                                {formData.permiteCancelamentoOnline !== 'Não Permitir' && (
                                    <div>
                                        <label className="text-sm font-medium text-gray-700 block mb-1">Antecedência para cancelar online</label>
                                        <PrazoInput 
                                            prazo={formData.prazoMaximoCancelamentoOnline} 
                                            onPrazoChange={(e) => onPrazoChange('prazoMaximoCancelamentoOnline', e)} 
                                        />
                                        <p className="text-xs text-gray-500 mt-1">O cliente só pode cancelar se for antes deste prazo.</p>
                                    </div>
                                )}
                            </div>

                            <div className="space-y-3">
                                <div>
                                    <label className="text-sm font-medium text-gray-700 block mb-1">Ação para Agendamentos Pendentes de Pgto</label>
                                    <select 
                                        name="acaoCancelamentoSemPagamento" 
                                        value={formData.acaoCancelamentoSemPagamento} 
                                        onChange={handleChange} 
                                        className="form-input"
                                    >
                                        <option value="Ser Avisado">Manter na Agenda e Avisar Recepção</option>
                                        <option value="Cancelar Automaticamente">Cancelar Automaticamente</option>
                                    </select>
                                </div>
                                
                                {formData.acaoCancelamentoSemPagamento === 'Cancelar Automaticamente' && (
                                    <div>
                                        <label className="text-sm font-medium text-gray-700 block mb-1">Tempo limite sem pagamento</label>
                                        <PrazoInput 
                                            prazo={formData.prazoCancelamentoSemPagamento} 
                                            onPrazoChange={(e) => onPrazoChange('prazoCancelamentoSemPagamento', e)} 
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </fieldset>

            <fieldset className="form-section">
                <legend className="form-section-title">Conclusão de Agendamento</legend>
                <div className="form-grid-container">
                    <div className="form-group">
                        <label className="text-sm font-medium text-gray-700 block mb-1">Como concluir os agendamentos desta agenda</label>
                        <select
                            name="acaoConclusaoAgendamento"
                            value={formData.acaoConclusaoAgendamento}
                            onChange={handleChange}
                            className="form-input"
                        >
                            <option value="Manual">Manual (a equipe clica em "Concluir")</option>
                            <option value="Automatico">Automático, após o horário de término</option>
                        </select>
                        <p className="form-hint">No automático, o agendamento é concluído sozinho (financeiro, pacote e mensagem de conclusão inclusos) depois do prazo abaixo.</p>
                    </div>

                    {formData.acaoConclusaoAgendamento === 'Automatico' && (
                        <div className="form-group">
                            <label className="text-sm font-medium text-gray-700 block mb-1">Concluir após (a partir do término)</label>
                            <PrazoInput
                                prazo={formData.prazoConclusaoAutomatica}
                                onPrazoChange={(e) => onPrazoChange('prazoConclusaoAutomatica', e)}
                            />
                        </div>
                    )}
                </div>
            </fieldset>

            <div className="form-actions mt-6">
                <button
                    type="submit"
                    className="action-button action-button-primary"
                    disabled={isLoading}
                >
                    {isLoading ? <Loader2 className="animate-spin icon" /> : <Save className="icon" />}
                    Salvar Configurações da Agenda
                </button>
            </div>
        </form>
    );
};

export default ConfiguracoesAgenda;